import { faCircleQuestion, faEyeSlash, faMinusSquare, faPlusSquare, faUserTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useState } from 'react';
import { Col, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { selectCurrentUserData, selectFerpaState } from '../../../../../features/authentication/authSlice';
import TableSkeleton from '../../../../../components/SkeletonLoader/TableSkeleton';
import AR_FloatingBar from '../components/ar_floatingBar';
import { SortUtils } from '../../../../../utils/sorting';
import { useGetAccessRightsMutation, useGetClassEditStatusMutation } from '../../../../../features/authentication/classesApiSlice';
import { useClassContext } from '../../../components/classContext';
import { useClassManagementContext } from '../utils/classManagementContext';
import Cookies from 'js-cookie';
import { faCheck, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { useUpdateAccessTypeMutation, useUpdateSelfGenTimeOnTaskMutation } from '../../../../../features/authentication/accessControlApiSlice';
import CustomToast from '../../../../../components/Toast';
import { useUpdateTextSelectionMutation, useUpdateReadSpeakerMutation } from '../../../../../features/authentication/studentApiSlice';
import { useNavigate, useParams } from 'react-router';

const AccessRightList = () => {
  const { setClassId } = useClassContext();
  const { selectedClassId, selectedCourseId, selectedCustomCourseId } = useClassManagementContext();
  const initialShowHiddenStudent = Cookies.get('displayRemovedStudents') === 'true';
  const navigate = useNavigate();

  const [getAccessRights] = useGetAccessRightsMutation();
  const [getClassEditStatus] = useGetClassEditStatusMutation();
  const [updateSelfGenTimeOnTaskApi] = useUpdateSelfGenTimeOnTaskMutation();
  const [updateAccessTypeApi] = useUpdateAccessTypeMutation();
  const [updateTextSelectionApi] = useUpdateTextSelectionMutation();
  const [updateReadSpeakerApi] = useUpdateReadSpeakerMutation();
  const user = useSelector(selectCurrentUserData);

  const [sortColumn, setSortColumn] = useState('lName');
  const [sortDirection, setSortDirection] = useState('asc');
  const [students, setStudents] = useState(null);
  const [allowedToEdit, setAllowedToEdit] = useState(false);
  const [enabledReadSpeaker, setEnabledReadSpeaker] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [triggerAction, setTriggerAction] = useState(false);
  const [dateBasedKey, setDateBasedKey] = useState(new Date().getTime().toString());
  const [showRemoved, setShowRemoved] = useState(initialShowHiddenStudent);
  const [checkAllStatus, setCheckAllStatus] = useState(false);
  const [isApiProcessing, setIsApiProcessing] = useState(false);
  const [showNote, setShowNote] = useState(false);

  const ferpaEnabled = useSelector(selectFerpaState);
  let { Id } = useParams();

  const [toggleStates, setToggleStates] = useState({
    activitiesAccess: false,
    textSelection: false,
    textToSpeech: false,
    selfGenTimeOnTask: false,
  });

  const getToggleStatus = (name) => {
    if (name == 'ActivitiesAccess')
      return toggleStates.activitiesAccess;
    else if (name == 'TextSelection')
      return toggleStates.textSelection;
    else if (name == 'TextToSpeech')
      return toggleStates.textToSpeech;
    else if (name == 'SelfGenTimeOnTask')
      return toggleStates.selfGenTimeOnTask;
  };

  const handleToggle = (name) => {
    if (name == 'ActivitiesAccess')
      toggleStates.activitiesAccess = !toggleStates.activitiesAccess;
    else if (name == 'TextSelection')
      toggleStates.textSelection = !toggleStates.textSelection;
    else if (name == 'TextToSpeech')
      toggleStates.textToSpeech = !toggleStates.textToSpeech;
    else if (name == 'SelfGenTimeOnTask')
      toggleStates.selfGenTimeOnTask = !toggleStates.selfGenTimeOnTask;

    setToggleStates(toggleStates);
    setDateBasedKey(new Date().getTime().toString());
  };

  useEffect(() => {
    Cookies.set('displayRemovedStudents', showRemoved, { expires: 7 });
    if (checkAllStatus && !showRemoved) {
      handleCheckAllChange(checkAllStatus);
    } else {
      updateCheckAllStatus();
    }
  }, [showRemoved]);

  useEffect(() => {
    if (selectedClassId) {
      getAllowedToEditStatus();
      setClassId(selectedClassId);
    }
  }, [selectedClassId]);

  useEffect(() => {
    if (!selectedCourseId || selectedCourseId <= 0) {
      navigate(`/classes/${Id}/classManagement/classManagement`);
      return;
    }
    if (selectedClassId) {
      loadStudents();
      setCheckAllStatus(false);
    }
  }, [selectedCourseId,selectedCustomCourseId,selectedClassId]);

  useEffect(() => {
    if (triggerAction)
      triggerSave();
  }, [triggerAction]);

  const renderSortIcons = (column) => {
    return (SortUtils.renderSortIcons(sortDirection, sortColumn, column));
  };

  const handleSort = async (column) => {
    if (!ferpaEnabled)
      return;
    var direction = sortColumn === column && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortColumn(column);
    setSortDirection(direction);
    let sortedStudent = await SortUtils.customSort(students, column, direction === 'asc');
    setStudents(sortedStudent);
  };

  const loadStudents = async () => {
    if (!selectedClassId || !selectedCourseId || selectedCourseId <= 0) {
      setStudents([]);
      return;
    }
    if (isApiProcessing)
      return;

    setStudents(null);
    setIsApiProcessing(true);
    const options = {
      id: selectedClassId,
      courseId: selectedCourseId == 255 ? selectedCustomCourseId : selectedCourseId
    };
    const studentList = await getAccessRights(options).unwrap();
    setStudents(studentList);
    setIsApiProcessing(false);
  };

  const getAllowedToEditStatus = async () => {
    var result = await getClassEditStatus(selectedClassId).unwrap();
    setAllowedToEdit(result.allowedToEdit);
    setEnabledReadSpeaker(result.enableReadSpeaker);
  };

  const handleDisplayRemoved = () => {
    setShowRemoved(!showRemoved);
  };

  const handleCheckAllChange = (checked) => {
    let modifiedStudents = [];
    modifiedStudents = students.map((item) => {
      return (item.deleted ? item : ((showRemoved || !item.deactivated) ? { ...item, isChecked: checked } : { ...item, isChecked: false }));
    });
    setStudents(modifiedStudents);
    updateCheckAllStatus(modifiedStudents);
  };

  const handleCheckBoxChange = (e) => {
    const { name, checked } = e.target;
    let modifiedStudents = [];
    const id = parseInt(name);
    modifiedStudents = students.map((item) =>
      item.studentId === id && !item.deleted ? { ...item, isChecked: checked } : item
    );
    setStudents(modifiedStudents);
    updateCheckAllStatus(modifiedStudents);
  };

  const updateCheckAllStatus = (modifiedStudents) => {
    setCheckAllStatus(getCheckAllStatus(modifiedStudents));
  };

  const getCheckAllStatus = (modifiedStudents) => {
    return (modifiedStudents?.filter((item) => (showRemoved || !item.deactivated) && !item.deleted && !item.isChecked).length < 1);
  };

  const fullBadge = () => {
    return (<div className="activeBadge align-content-center">Full</div>);
  };

  const assignedBadge = () => {
    return (<div className="disableBadge align-content-center" >Assigned</div>);
  };

  const handleAction = (actionType, students) => {
    setSelectedAction(actionType);
    setSelectedStudents(students);
    handleToggle(actionType);
    setTriggerAction(true);
  };

  const triggerSave = () => {
    if (selectedAction == 'ActivitiesAccess') {
      updateActivitiesAccess(selectedAction, selectedStudents);
    }
    else if (selectedAction == 'TextSelection') {
      updateTextSelection(selectedAction, selectedStudents);
    }
    else if (selectedAction == 'TextToSpeech') {
      updateTextToSpeech(selectedAction, selectedStudents);
    }
    else if (selectedAction == 'SelfGenTimeOnTask') {
      updateSelfGenTimeOnTask(selectedAction, selectedStudents);
    }
    else {
      console.log('Invalid Action');
    }
  };

  const updateActivitiesAccess = async (actionType, selectedStudents) => {
    const classId = parseInt(selectedClassId);
    const courseId = parseInt(selectedCourseId == 255 ? selectedCustomCourseId : selectedCourseId);
    const selectedStudentIds = selectedStudents.map(x => x.studentId);
    const status = getToggleStatus(actionType);

    if (!classId || classId <= 0 || !courseId || courseId <= 0 || !selectedStudentIds || selectedStudentIds.length == 0) {
      return;
    }
    setIsApiProcessing(true);
    const options =
    {
      studentIds: selectedStudentIds,
      classId: classId,
      courseId: courseId,
      status: status
    };
    var response = await updateAccessTypeApi(options);

    if (response.error?.data?.errors) {
      CustomToast(response.error.data.title, 'error');
      return;
    } else if (response.error) {
      CustomToast(response.error.data.error, 'error');
      return;
    }
    CustomToast('Selected Student(s) Activities Access Status Updated', 'success');
    setStudents(students.map((student) => {
      return (selectedStudentIds.includes(student.studentId) ? { ...student, accessType: status ? 1 : 0 , accessSetBy: user?.LastName + ' '+ user?.FirstName  } : { ...student });
    }));
    setIsApiProcessing(false);
    setTriggerAction(false);
  };

  const updateTextSelection = async (actionType, selectedStudents) => {
    const classId = parseInt(selectedClassId);
    const courseId = parseInt(selectedCourseId == 255 ? selectedCustomCourseId : selectedCourseId);
    const selectedStudentIds = selectedStudents.map(x => x.studentId);
    const status = getToggleStatus(actionType);

    if (!classId || classId <= 0 || !courseId || courseId <= 0 || !selectedStudentIds || selectedStudentIds.length == 0) {
      return;
    }
    setIsApiProcessing(true);
    const options =
    {
      studentIds: selectedStudentIds,
      classId: classId,
      courseId: courseId,
      status: status
    };
    var response = await updateTextSelectionApi(options);

    if (response.error?.data?.errors) {
      CustomToast(response.error.data.title, 'error');
      return;
    } else if (response.error) {
      CustomToast(response.error.data.error, 'error');
      return;
    }
    CustomToast('Selected Student(s) Text Selection Status Updated', 'success');
    setStudents(students.map((student) => {
      return (selectedStudentIds.includes(student.studentId) ? { ...student, textSelection: status } : { ...student });
    }));
    setIsApiProcessing(false);
    setTriggerAction(false);
  };

  const updateTextToSpeech = async (actionType, selectedStudents) => {
    const classId = parseInt(selectedClassId);
    const courseId = parseInt(selectedCourseId == 255 ? selectedCustomCourseId : selectedCourseId);
    const selectedStudentIds = selectedStudents.map(x => x.studentId);
    const status = getToggleStatus(actionType);

    if (!classId || classId <= 0 || !courseId || courseId <= 0 || !selectedStudentIds || selectedStudentIds.length == 0) {
      return;
    }
    setIsApiProcessing(true);
    const options =
    {
      studentIds: selectedStudentIds,
      classId: classId,
      courseId: courseId,
      status: status
    };
    var response = await updateReadSpeakerApi(options);

    if (response.error?.data?.errors) {
      CustomToast(response.error.data.title, 'error');
      return;
    } else if (response.error) {
      CustomToast(response.error.data.error, 'error');
      return;
    }
    CustomToast('Selected Student(s) Text-To-Speech Status Updated', 'success');
    setStudents(students.map((student) => {
      return (selectedStudentIds.includes(student.studentId) ? { ...student, readSpeaker: status } : { ...student });
    }));
    setIsApiProcessing(false);
    setTriggerAction(false);
  };

  const updateSelfGenTimeOnTask = async (actionType, selectedStudents) => {
    const classId = parseInt(selectedClassId);
    const courseId = parseInt(selectedCourseId == 255 ? selectedCustomCourseId : selectedCourseId);
    const selectedStudentIds = selectedStudents.map(x => x.studentId);
    const status = getToggleStatus(actionType);

    if (!classId || classId <= 0 || !courseId || courseId <= 0 || !selectedStudentIds || selectedStudentIds.length == 0) {
      return;
    }
    setIsApiProcessing(true);
    const options =
    {
      studentIds: selectedStudentIds,
      classId: classId,
      courseId: courseId,
      status: status
    };
    var response = await updateSelfGenTimeOnTaskApi(options);

    if (response.error?.data?.errors) {
      CustomToast(response.error.data.title, 'error');
      return;
    } else if (response.error) {
      CustomToast(response.error.data.error, 'error');
      return;
    }
    CustomToast('Selected Student(s) Self Gen Time On Task Status Updated', 'success');
    setStudents(students.map((student) => {
      return (selectedStudentIds.includes(student.studentId) ? { ...student, selfGenTimeOnTask: status } : { ...student });
    }));
    setIsApiProcessing(false);
    setTriggerAction(false);
  };


  return (
    <>
      <div >

        <Row>
          <Col>
            <div className='d-flex justify-content-between mb-4'>
              <div className='fw-medium'>
                Number Of students <span className="rounded-number mx-1 fw-semibold">{students ? (showRemoved ? students.length : students.filter(x => !x.deleted && !x.deactivated).length) : 0}</span>
              </div>
              <div className='hideForPrint'>
                <input
                  type="checkbox"
                  className="form-check-input me-2 cursor-pointer"
                  checked={showRemoved}
                  id="flexCheckDefault"
                  name="allselect"
                  onChange={() => handleDisplayRemoved()}
                />
                <label className="form-check-label">
                  Display Removed Students
                  <OverlayTrigger
                    key={'assignment-options'}
                    placement="top"
                    overlay={
                      <Tooltip className="initial-assignment-tooltip">
                        <div>
                          Removed students include those that are Hidden (by teacher), Disabled (by district), or Deactivated (based on account activity or manual deactivation)
                        </div>
                      </Tooltip>
                    }>
                    <FontAwesomeIcon className="circle-question px-2" icon={faCircleQuestion} />
                  </OverlayTrigger>
                </label>
              </div>
            </div>
          </Col>
        </Row>
        <Row className=''>
          <Col className='d-flex align-items-center'>
            <div>
              <span className="flex-shrink-0">
                <i className="fa-duotone fa-circle-info fs-2 mr-2 clr-fa-circle-question"></i>
              </span>
            </div>
            <div id="reportOptionsDiv" className="fw-medium d-flex align-item-center info-toggle cursor-pointer gap-2" onClick={() => setShowNote(!showNote)}>
              <div>Notes</div>
              <div>
                {showNote ? (
                  <FontAwesomeIcon icon={faMinusSquare} />
                ) : (
                  <FontAwesomeIcon icon={faPlusSquare} />
                )}
              </div>
            </div>
          </Col>
        </Row>
        <Row className='mb-3'>
          <Col className='ml-3'>
            {showNote && <div>
              <span>You may want to fully control student access to questions, limiting them to those in assignments that you create. Set access to <span className='fw-medium'>Assigned</span> to prevent students from creating any self-generated assignments. Set access to <span className='fw-medium'>Full</span> to allow students to create assignments. Note that DBQ activities are only available for assignments assigned by a teacher.<br /><br />
                By default, students are prevented from selecting (highlighting) text so that they can&apos;t copy the questions and enter them into a search service. Preventing Text Selection also disables the browser’s automatic language translation feature. If this interferes with text-to-speech or other necessary applications, then set <span className='fw-medium'>Text Selection</span> to <span className='fw-medium'>Enabled.</span>
              </span>
            </div>
            }
          </Col>
        </Row>
        <Row>
          <Col className='table-container transparent-scroll'>
            <table className='table reportTable'>
              {students ? (
                <>
                  <thead className='thead-light align-middle table-head thead-fixed'>
                    <tr>
                      {allowedToEdit && <th className=''>
                        <div className="form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            value=""
                            id="assignCheck"
                            name="assignCheck"
                            checked={checkAllStatus}
                            onChange={(e) => handleCheckAllChange(e.target.checked)}
                          />
                        </div>
                      </th>
                      }
                      <th scope="col" className={`fw-medium ${ferpaEnabled ? 'cursor-pointer' : ''}`} onClick={() => handleSort('lName')}>
                        <span className='align-items-center d-flex justify-content-between'> <span>Last Name</span> {ferpaEnabled && renderSortIcons('lName')} </span>
                      </th>
                      <th scope="col" className={`fw-medium ${ferpaEnabled ? 'cursor-pointer' : ''}`} onClick={() => handleSort('fName')}>
                        <span className='align-items-center d-flex justify-content-between'><span>First Name</span> {ferpaEnabled && renderSortIcons('fName')}</span>
                      </th>
                      <th scope="col" className={'fw-medium'} >
                        <span className='align-items-center'>Activities Access&nbsp;
                          <OverlayTrigger
                            key="visibilityStatus"
                            placement="left"

                            overlay={<Tooltip className="custom-tooltip">
                              <div className='w-100'>
                                You can control whether or not students can access content that have not been assigned to them. Access can be <b>Full</b> (allowing student access on their own) or <b>Assigned</b> (allowing student access only when assigned to them).
                                <br /><br />To change access for one or more students, check the checkboxes for the appropriate students then click <span className='fw-medium'>Activity Access</span> Button, found below the table.
                                <br /><br />Hover next to an individual status if you think that another teacher may have changed the access for a student. The name of the last teacher to modify the status is displayed. Note that if no teacher has explicitly set the access status, the default value, <b>Full</b>, is used.
                              </div>
                            </Tooltip>}>
                            <FontAwesomeIcon icon={faCircleQuestion} color="#88CBF1" />
                          </OverlayTrigger>
                        </span>
                      </th>
                      <th scope="col" className={'fw-medium'}>
                        <span className='align-items-center'>Text Selection&nbsp;
                          <OverlayTrigger
                            key="visibilityStatus"
                            placement="left"

                            overlay={<Tooltip className="custom-tooltip">
                              <div className='w-100'>
                                When <b>Text Selection</b> is enabled, students can select (highlight) question text.  Once text is selected they can copy the text and paste it into another browser window.  Doing this allows the student to easily search for answers using search services.  Additionally, when enabled, students can display a context menu that allows the selected text to easily be translated between foreign languages and English.  <br /><b>Please note that when text selection is disabled it may prevent some Text-To-Speech applications from functioning properly.  If this is the case you should enable text selection.</b>
                              </div>
                            </Tooltip>}>
                            <FontAwesomeIcon icon={faCircleQuestion} color="#88CBF1" />
                          </OverlayTrigger>
                        </span>
                      </th>
                      {enabledReadSpeaker && <th scope="col" className={'fw-medium'}>
                        <span className='align-items-center'>Text-to-Speech / Reading Passages
                          &nbsp;
                          <OverlayTrigger
                            key="visibilityStatus"
                            placement="left"

                            overlay={<Tooltip className="custom-tooltip">
                              <div className='w-100'>
                                When <b>Text-to-Speech for Reading Passages</b> is enabled for students, the control will be added to the Reading Selections.<br /><br />
                                Questions for Courses in foreign languages: Beginning Spanish, Spanish and French will not show the control regardless if the option is enabled or not.<br /><br />
                                Select the students for which you want to enable or disable Text-to-Speech and click the appropriate button below.
                              </div>
                            </Tooltip>}>
                            <FontAwesomeIcon icon={faCircleQuestion} color="#88CBF1" />
                          </OverlayTrigger>
                        </span>
                      </th>
                      }
                      <th scope="col" className={'fw-medium'}>
                        <div className='align-items-center'>Self Gen Time On Task&nbsp;
                          <OverlayTrigger
                            key="visibilityStatus"
                            placement="left"

                            overlay={<Tooltip className="custom-tooltip">
                              <div className='w-100'>
                                When enabled students will have 60 seconds of idle time on a question before being prompted to continue. This allows us to track the amount of time spent on a question.
                              </div>
                            </Tooltip>}>
                            <FontAwesomeIcon icon={faCircleQuestion} color="#88CBF1" />
                          </OverlayTrigger>
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {students?.filter(x => (showRemoved || (!x.deleted && !x.deactivated))).map((item) => (
                      <tr key={`record_${item.studentId}`}>
                        {allowedToEdit && <td className=''>
                          <div className='d-flex align-items-center'>
                            {!item.deleted && (
                              <div className="form-check">
                                <input
                                  type="checkbox"
                                  value=""
                                  name={item.studentId}
                                  checked={item?.isChecked}
                                  onChange={handleCheckBoxChange}
                                  className="form-check-input"
                                  id={`flexCheckDefault-${item.studentId}`}
                                />
                              </div>
                            )}
                            {item.deleted &&
                              <OverlayTrigger
                                key={`deleted_${item.studentId}`}
                                placement="top"
                                overlay={<Tooltip className="custom-tooltip">Hidden
                                </Tooltip>}>
                                <div className='hiddenStudent'>
                                  <FontAwesomeIcon icon={faEyeSlash} className='p-1' />
                                </div>
                              </OverlayTrigger>
                            }
                            {item.deactivated &&
                              <OverlayTrigger
                                key={`deactivated_${item.studentId}`}
                                placement="top"
                                overlay={<Tooltip className="custom-tooltip">Deactivated
                                </Tooltip>}>
                                <div className='deactiveStudent'>
                                  <FontAwesomeIcon icon={faUserTimes} className='p-1' />
                                </div>
                              </OverlayTrigger>}
                          </div>
                        </td>
                        }
                        <td>{ferpaEnabled ? item.lName : <i>Hidden</i>}</td>
                        <td>{ferpaEnabled ? item.fName : <i>Hidden</i>}</td>
                        <td>
                          <div className='d-flex justify-content-center'>
                            <div className='align-items-center d-flex gap-2'>
                              {item.accessType == 0 ? fullBadge() : assignedBadge()}
                              <div>
                                <OverlayTrigger
                                  key="visibilityStatus"
                                  placement="right"
                                  overlay={<Tooltip className="custom-tooltip">
                                    <div className='w-100'>
                                      {item.accessSetBy != null ?(`The access for the student was set by ${item.accessSetBy}.`):'The access for the student is set to the default value.'}
                                    </div>
                                  </Tooltip>}>
                                  <FontAwesomeIcon icon={faCircleQuestion} color="#88CBF1" />
                                </OverlayTrigger>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className='text-center'>
                          <button className={`fs-5 p-0 btn grid-icon-btn ${item.textSelection ? 'success-icon-btn' : 'danger-icon-btn'} }`}>
                            <FontAwesomeIcon icon={item.textSelection ? faCheck : faXmark} className='p-1' />
                          </button>
                        </td>
                        {enabledReadSpeaker && <td className='text-center'>
                          <button className={`fs-5 p-0 btn grid-icon-btn ${item.readSpeaker ? 'success-icon-btn' : 'danger-icon-btn'} }`}>
                            <FontAwesomeIcon icon={item.readSpeaker ? faCheck : faXmark} className='p-1' />
                          </button>
                        </td>
                        }
                        <td className='text-center'>
                          <button className={`fs-5 p-0 btn grid-icon-btn ${item.selfGenTimeOnTask ? 'success-icon-btn' : 'danger-icon-btn'} }`}>
                            <FontAwesomeIcon icon={item.selfGenTimeOnTask ? faCheck : faXmark} className='p-1' />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {students?.filter(x => showRemoved || (!x.deleted && !x.deactivated)).length == 0 && <tr>
                      <td colSpan='5' className='p-3 text-danger'>There are no students in the class.</td>
                    </tr>}
                  </tbody>
                </>
              ) : (
                <>
                  <table className='table reportTable w-100'>
                    <TableSkeleton
                      colCount={5}
                      rowCount={4} />
                  </table>
                </>
              )}
            </table>
          </Col>
        </Row>
      </div>
      <AR_FloatingBar key={'floatingBar' + dateBasedKey} selectedStudents={students?.filter(x => x.isChecked)} onAction={handleAction} allowedToEdit={allowedToEdit} enabledReadSpeaker={enabledReadSpeaker} getToggleStatus={getToggleStatus} isApiProcessing={isApiProcessing} />
    </>
  );
};

export default AccessRightList;